
// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    const bid = await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id');
    if (bid) return bid;
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) { params.set('company_id', companyId); params.set('company_id', companyId); }
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' };
    if (token) headers.Authorization = 'Bearer ' + token;
    const response = await fetch(url, {
      headers,
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====

/* eslint-disable no-unused-vars */
// VideoInterviewManagementScreen_Production.js
// Comprehensive production-ready video interview management screen with advanced features,
// scheduling, analytics, team collaboration, and professional interface

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Modal,
  TextInput,
  ScrollView,
  Alert,
  Dimensions,
  SafeAreaView,
  RefreshControl,
  ActivityIndicator,
  StatusBar,
  Animated,
  Image,
} from 'react-native';
import {
  MaterialCommunityIcons,
  FontAwesome5,
  Ionicons,
  AntDesign,
  Feather,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width, height } = Dimensions.get('window');

const VideoInterviewManagementScreen = ({ navigation, route }) => {
  const jobId = route?.params?.jobId || route?.params?.job_id || null;
  const jobTitle = route?.params?.jobTitle || '';
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');

  // State management
  const [interviews, setInterviews] = useState([]);
  const [filteredInterviews, setFilteredInterviews] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modal states
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [analyticsModalVisible, setAnalyticsModalVisible] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [bulkActionModalVisible, setBulkActionModalVisible] = useState(false);
  
  // Selected items
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [selectedInterviews, setSelectedInterviews] = useState([]);
  const [bulkSelectionMode, setBulkSelectionMode] = useState(false);
  
  // Statistics
  const [statistics, setStatistics] = useState({
    total: 0,
    scheduled: 0,
    completed: 0,
    cancelled: 0,
    pending: 0,
    averageRating: 0,
    averageDuration: 0,
    successRate: 0,
  });
  
  // Current user and company data
  const [currentUser, setCurrentUser] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  
  // New interview form state
  const [newInterview, setNewInterview] = useState({
    applicantName: '',
    applicantEmail: '',
    applicantPhone: '',
    position: '',
    department: '',
    date: '',
    time: '',
    duration: '60',
    interviewType: 'technical',
    priority: 'medium',
    questions: ['', '', '', '', ''],
    interviewers: [],
    notes: '',
    recordingEnabled: true,
    aiAnalysisEnabled: true,
    transcriptionEnabled: true,
  });

  // Animation values
  const [fadeAnimation] = useState(new Animated.Value(0));
  const [slideAnimation] = useState(new Animated.Value(0));

  // Helper functions for safe data access
  const safeGetString = (value, fallback = '') => {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      if (value.name) return String(value.name);
      if (value.full_name) return String(value.full_name);
      if (value.title) return String(value.title);
    }
    return fallback;
  };

  const safeGetNumber = (value, fallback = 0) => {
    if (typeof value === 'number' && !isNaN(value)) return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return !isNaN(parsed) ? parsed : fallback;
    }
    return fallback;
  };

  const safeGetArray = (value, fallback = []) => {
    return Array.isArray(value) ? value : fallback;
  };

  // Load data from AsyncStorage
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load user data
      const userData = await AsyncStorage.getItem('currentUser');
      if (userData) {
        setCurrentUser(JSON.parse(userData));
      }

      // Load company data
      const companyInfo = await AsyncStorage.getItem('companyProfile');
      if (companyInfo) {
        setCompanyData(JSON.parse(companyInfo));
      }

      // Load interviews from the canonical /api/recruitment/interviews endpoint.
      // This reads from application_interviews — the SAME table the candidate's
      // VideoInterviewScreen reads from. Both sides see each other's writes
      // (status changes, candidate_joined_at, recording_url, feedback).
      let interviewsLoaded = false;
      try {
        const companyId = await getCompanyId();
        if (companyId) {
          const token = await AsyncStorage.getItem('auth_token').catch(() => null);
          const r = await fetch(`${API_BASE_URL}/api/recruitment/interviews?company_id=${companyId}&limit=200`, {
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          if (r.ok) {
            const rows = await r.json();
            if (Array.isArray(rows)) {
              // Normalize DB row shape → the UI's expected fields. The list view
              // reads camelCase keys with a few specific names; map them here so
              // the rest of the screen doesn't need rewriting.
              const normalized = rows.map(row => ({
                id: String(row.id),
                applicationId: row.application_id,
                applicantName:  row.candidate_name || 'Applicant',
                applicantEmail: row.candidate_email || '',
                applicantPhone: row.candidate_phone || '',
                applicantAvatar: row.candidate_avatar || null,
                position: row.job_title || '',
                department: '',
                date:  row.scheduled_date ? new Date(row.scheduled_date).toISOString().slice(0, 10) : '',
                time:  row.scheduled_date ? new Date(row.scheduled_date).toISOString().slice(11, 16) : '',
                scheduledTime: row.scheduled_date,
                duration: String(row.duration_minutes || 60),
                interviewType: row.interview_type || 'video',
                status: row.status || 'scheduled',
                rating: Number(row.rating || 0),
                feedback: row.feedback || row.interviewer_notes || '',
                interviewers: Array.isArray(row.interviewers) ? row.interviewers : [],
                meetingLink: row.meeting_link || '',
                location: row.location || '',
                instructions: row.instructions || '',
                roundNumber: row.round_number || 1,
                recordingEnabled: !!row.recording_enabled,
                recordingUrl: row.recording_url || null,
                candidateJoinedAt: row.candidate_joined_at || null,
                candidateLeftAt:   row.candidate_left_at || null,
                candidateFeedback: row.candidate_feedback || null,
                candidateNotes:    row.candidate_notes || '',
                decision: row.decision || null,
                applicationStage:  row.application_stage,
                createdAt: row.created_at,
                updatedAt: row.updated_at,
              }));
              setInterviews(normalized);
              calculateStatistics(normalized);
              await AsyncStorage.setItem('videoInterviews', JSON.stringify(normalized));
              interviewsLoaded = true;
            }
          }
        }
      } catch (apiError) {
        console.warn('API unavailable:', apiError?.message);
      }
      if (!interviewsLoaded) {
        // Offline cache fallback only — never substitute mock data
        const storedInterviews = await AsyncStorage.getItem('videoInterviews');
        if (storedInterviews) {
          const parsedInterviews = JSON.parse(storedInterviews);
          setInterviews(parsedInterviews);
          calculateStatistics(parsedInterviews);
        } else {
          setInterviews([]);
          calculateStatistics([]);
        }
      }

      // Start animations
      Animated.parallel([
        Animated.timing(fadeAnimation, {
          toValue: 1,
          duration: 800,
          useNativeDriver: false,
        }),
        Animated.timing(slideAnimation, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: false,
        }),
      ]).start();

    } catch (error) {
      console.error('Error loading interview data:', error);
      Alert.alert('Error', 'Failed to load interview data');
    } finally {
      setLoading(false);
    }
  }, [fadeAnimation, slideAnimation]);

  // Generate sample interviews
  const generateSampleInterviews = () => {
    const positions = ['Senior Software Engineer', 'UX Designer', 'DevOps Engineer', 'Product Manager', 'Data Scientist', 'Frontend Developer', 'Backend Developer', 'Full Stack Developer'];
    const departments = ['Engineering', 'Design', 'Operations', 'Product', 'Data Science'];
    const statuses = ['scheduled', 'completed', 'cancelled', 'pending'];
    const priorities = ['high', 'medium', 'low'];
    const interviewTypes = ['technical', 'behavioral', 'cultural', 'final'];
    
    const sampleInterviews = [];
    
    for (let i = 1; i <= 15; i++) {
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      const position = positions[Math.floor(Math.random() * positions.length)];
      const department = departments[Math.floor(Math.random() * departments.length)];
      const priority = priorities[Math.floor(Math.random() * priorities.length)];
      const interviewType = interviewTypes[Math.floor(Math.random() * interviewTypes.length)];
      
      const baseDate = new Date();
      baseDate.setDate(baseDate.getDate() + Math.floor(Math.random() * 30) - 15);
      
      sampleInterviews.push({
        id: i.toString(),
        applicantName: `Applicant ${i}`,
        applicantEmail: `applicant${i}@example.com`,
        applicantPhone: `+44 7700 90${i.toString().padStart(4, '0')}`,
        position,
        department,
        status,
        priority,
        interviewType,
        date: baseDate.toISOString().split('T')[0],
        time: `${9 + Math.floor(Math.random() * 8)}:${Math.random() > 0.5 ? '00' : '30'}`,
        duration: [30, 45, 60, 90][Math.floor(Math.random() * 4)],
        scheduledTime: baseDate.toISOString(),
        createdAt: new Date(baseDate.getTime() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
        questions: [
          'Tell us about your experience with this technology stack',
          'How do you approach problem-solving in complex projects?',
          'Describe a challenging situation you faced and how you resolved it',
          'What interests you most about this role and our company?',
          'Where do you see yourself in the next 5 years?',
        ],
        interviewers: [
          { id: '1', name: 'John Smith', role: 'Engineering Manager' },
          { id: '2', name: 'Sarah Johnson', role: 'HR Director' },
        ],
        notes: status === 'completed' ? 'Strong technical skills, good communication, recommended for next round.' : '',
        rating: status === 'completed' ? Math.floor(Math.random() * 5) + 1 : 0,
        feedback: status === 'completed' ? 'Excellent applicant with strong problem-solving skills and relevant experience.' : '',
        recordingEnabled: true,
        aiAnalysisEnabled: true,
        transcriptionEnabled: true,
        recordingUrl: status === 'completed' ? `https://recordings.jobeng.com/interview_${i}.mp4` : null,
        transcriptUrl: status === 'completed' ? `https://transcripts.jobeng.com/interview_${i}.txt` : null,
        aiAnalysisUrl: status === 'completed' ? `https://analysis.jobeng.com/interview_${i}.json` : null,
      });
    }
    
    return sampleInterviews;
  };

  // Calculate statistics
  const calculateStatistics = (interviewList) => {
    const total = interviewList.length;
    const scheduled = interviewList.filter(i => i.status === 'scheduled').length;
    const completed = interviewList.filter(i => i.status === 'completed').length;
    const cancelled = interviewList.filter(i => i.status === 'cancelled').length;
    const pending = interviewList.filter(i => i.status === 'pending').length;
    
    const completedInterviews = interviewList.filter(i => i.status === 'completed');
    const averageRating = completedInterviews.length > 0 
      ? completedInterviews.reduce((sum, i) => sum + safeGetNumber(i.rating, 0), 0) / completedInterviews.length 
      : 0;
    
    const averageDuration = completedInterviews.length > 0
      ? completedInterviews.reduce((sum, i) => sum + safeGetNumber(i.duration, 0), 0) / completedInterviews.length
      : 0;
    
    const successRate = total > 0 ? (completed / total) * 100 : 0;
    
    setStatistics({
      total,
      scheduled,
      completed,
      cancelled,
      pending,
      averageRating: Math.round(averageRating * 10) / 10,
      averageDuration: Math.round(averageDuration),
      successRate: Math.round(successRate * 10) / 10,
    });
  };

  // Filter and sort interviews
  const filterAndSortInterviews = useCallback(() => {
    let filtered = [...interviews];
    
    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(interview => interview.status === filterStatus);
    }
    
    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(interview =>
        safeGetString(interview.applicantName, '').toLowerCase().includes(query) ||
        safeGetString(interview.position, '').toLowerCase().includes(query) ||
        safeGetString(interview.department, '').toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.scheduledTime || a.createdAt);
          bValue = new Date(b.scheduledTime || b.createdAt);
          break;
        case 'name':
          aValue = safeGetString(a.applicantName, '').toLowerCase();
          bValue = safeGetString(b.applicantName, '').toLowerCase();
          break;
        case 'position':
          aValue = safeGetString(a.position, '').toLowerCase();
          bValue = safeGetString(b.position, '').toLowerCase();
          break;
        case 'rating':
          aValue = safeGetNumber(a.rating, 0);
          bValue = safeGetNumber(b.rating, 0);
          break;
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setFilteredInterviews(filtered);
  }, [interviews, filterStatus, searchQuery, sortBy, sortOrder]);

  // Refresh data
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, [loadData]);

  // Load data on component mount
    // Company logo (same as EditSupplierScreen)
  useFocusEffect(React.useCallback(() => { loadCompanyLogo(); }, []));
  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
        if (profile.name || profile.company_name) setCompanyName(profile.name || profile.company_name);
      }
    } catch (e) { console.warn('Logo load error:', e.message); }
  };

useEffect(() => {
    loadData();
  }, [loadData]);

  // Update filtered interviews when filters change
  useEffect(() => {
    filterAndSortInterviews();
  }, [filterAndSortInterviews]);

  // Check for feedback data from interview screen
  useEffect(() => {
    if (route.params?.feedbackSubmitted && route.params?.interviewId) {
      // Update interview with feedback data
      const updatedInterviews = interviews.map(interview =>
        interview.id === route.params.interviewId
          ? { ...interview, status: 'completed' }
          : interview
      );
      setInterviews(updatedInterviews);
      calculateStatistics(updatedInterviews);

      Alert.alert(
        'Interview Completed',
        'The interview has been completed and feedback has been saved.',
        [{ text: 'OK' }]
      );
    }
  }, [route.params, interviews]);

  // Handle adding a new interview
  const handleAddInterview = async () => {
    // Validate form
    if (
      !newInterview.applicantName ||
      !newInterview.applicantEmail ||
      !newInterview.position ||
      !newInterview.date ||
      !newInterview.time
    ) {
      Alert.alert('Missing Information', 'Please fill in all required fields');
      return;
    }

    try {
      // Create new interview
      const newId = (interviews.length + 1).toString();
      const scheduledDateTime = new Date(`${newInterview.date}T${newInterview.time}`);
      
      const interviewToAdd = {
        ...newInterview,
        id: newId,
        scheduledTime: scheduledDateTime.toISOString(),
        createdAt: new Date().toISOString(),
        status: 'pending',
        rating: 0,
        feedback: '',
        questions: newInterview.questions.filter(q => q.trim() !== ''),
        recordingUrl: null,
        transcriptUrl: null,
        aiAnalysisUrl: null,
      };

      const updatedInterviews = [...interviews, interviewToAdd];
      setInterviews(updatedInterviews);
      calculateStatistics(updatedInterviews);
      
      // Save to AsyncStorage
      try { await apiRequest('video-interviews', { method: 'PUT', body: { interviews: updatedInterviews } }); } catch(e) { console.warn('API save failed:', e.message); }
      await AsyncStorage.setItem('videoInterviews', JSON.stringify(updatedInterviews));

      // Reset form and close modal
      setNewInterview({
        applicantName: '',
        applicantEmail: '',
        applicantPhone: '',
        position: '',
        department: '',
        date: '',
        time: '',
        duration: '60',
        interviewType: 'technical',
        priority: 'medium',
        questions: ['', '', '', '', ''],
        interviewers: [],
        notes: '',
        recordingEnabled: true,
        aiAnalysisEnabled: true,
        transcriptionEnabled: true,
      });
      setModalVisible(false);
      
      Alert.alert('Success', 'Interview scheduled successfully!');
    } catch (error) {
      console.error('Error adding interview:', error);
      Alert.alert('Error', 'Failed to schedule interview');
    }
  };

  // Handle updating interview status
  // PUTs to /api/data/application_interviews/:id so the candidate sees the
  // status change immediately on their VideoInterviewScreen. Also writes
  // candidate_joined_at/left_at where relevant so both sides have the same
  // join history.
  const handleStatusChange = async (id, newStatus) => {
    try {
      // Build the patch — include side-effect fields where the status implies them
      const patch = { status: newStatus, updated_at: new Date().toISOString() };
      if (newStatus === 'in_progress') patch.candidate_joined_at = new Date().toISOString();
      if (newStatus === 'completed' || newStatus === 'cancelled') patch.candidate_left_at = new Date().toISOString();

      const token = await AsyncStorage.getItem('auth_token').catch(() => null);
      const r = await fetch(`${API_BASE_URL}/api/data/application_interviews/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(`Server returned ${r.status}`);

      const updatedInterviews = interviews.map(interview =>
        interview.id === id ? { ...interview, status: newStatus, ...patch } : interview
      );
      setInterviews(updatedInterviews);
      calculateStatistics(updatedInterviews);
      await AsyncStorage.setItem('videoInterviews', JSON.stringify(updatedInterviews));

      if (selectedInterview && selectedInterview.id === id) {
        setSelectedInterview({ ...selectedInterview, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating interview status:', error);
      Alert.alert('Error', 'Failed to update interview status');
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    try {
      let updatedInterviews = [...interviews];
      
      switch (action) {
        case 'delete':
          updatedInterviews = updatedInterviews.filter(
            interview => !selectedInterviews.includes(interview.id)
          );
          break;
        case 'cancel':
          updatedInterviews = updatedInterviews.map(interview =>
            selectedInterviews.includes(interview.id)
              ? { ...interview, status: 'cancelled' }
              : interview
          );
          break;
        case 'reschedule':
          // This would open a reschedule modal in a real app
          Alert.alert('Reschedule', 'Reschedule functionality would be implemented here');
          return;
      }
      
      setInterviews(updatedInterviews);
      calculateStatistics(updatedInterviews);
      try { await apiRequest('video-interviews', { method: 'PUT', body: { interviews: updatedInterviews } }); } catch(e) { console.warn('API save failed:', e.message); }
      await AsyncStorage.setItem('videoInterviews', JSON.stringify(updatedInterviews));
      
      setSelectedInterviews([]);
      setBulkSelectionMode(false);
      setBulkActionModalVisible(false);
      
      Alert.alert('Success', `Bulk action completed for ${selectedInterviews.length} interviews`);
    } catch (error) {
      console.error('Error performing bulk action:', error);
      Alert.alert('Error', 'Failed to perform bulk action');
    }
  };

  // Handle joining a video interview
  const handleJoinInterview = (interview) => {
    // Convert interview data to format expected by VideoInterviewScreen
    const formattedInterview = {
      id: interview.id,
      applicant: {
        id: `cand${interview.id}`,
        name: safeGetString(interview.applicantName, 'Unknown Applicant'),
        email: safeGetString(interview.applicantEmail, 'unknown@example.com'),
        phone: safeGetString(interview.applicantPhone, 'N/A'),
        position: safeGetString(interview.position, 'Unknown Position'),
        experience: 'N/A',
        photo: null,
        skills: ['JavaScript', 'React', 'Node.js', 'Python'], // Sample skills
        education: 'Bachelor\'s Degree in Computer Science',
        location: 'London, UK',
      },
      interviewers: safeGetArray(interview.interviewers, [
        { id: 'int1', name: 'Current User', role: 'Interviewer', department: 'HR' },
      ]),
      scheduledTime: interview.scheduledTime || new Date().toISOString(),
      duration: safeGetNumber(interview.duration, 60),
      status: interview.status === 'scheduled' ? 'Scheduled' : 'Pending',
      videoLink: 'https://meet.jobeng.com/interview-room',
      questions: safeGetArray(interview.questions, []).map((q, index) => ({
        id: `q${index + 1}`,
        text: q,
        category: index % 2 === 0 ? 'Technical' : 'Behavioral',
        difficulty: ['Easy', 'Medium', 'Hard'][index % 3],
        notes: '',
      })),
      notes: safeGetString(interview.notes, ''),
      recordingEnabled: interview.recordingEnabled !== false,
      aiAnalysisEnabled: interview.aiAnalysisEnabled !== false,
      transcriptionEnabled: interview.transcriptionEnabled !== false,
    };

    // Navigate to the VideoInterviewScreen with interview data
    navigation.navigate('BusinessVideoInterviewScreen', {
      interviewId: interview.id,
      interviewData: formattedInterview,
    });
  };

  // Format date and time
  const formatDateTime = (dateString, timeString) => {
    try {
      const date = new Date(dateString);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      let dateLabel;
      if (date.toDateString() === today.toDateString()) {
        dateLabel = 'Today';
      } else if (date.toDateString() === tomorrow.toDateString()) {
        dateLabel = 'Tomorrow';
      } else {
        dateLabel = date.toLocaleDateString();
      }
      
      return `${dateLabel} at ${timeString}`;
    } catch (error) {
      return `${dateString} at ${timeString}`;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#4A90E2';
      case 'completed': return '#10B981';
      case 'cancelled': return '#EF4444';
      case 'pending': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      case 'low': return '#10B981';
      default: return '#6B7280';
    }
  };

  // Get interview type icon
  const getInterviewTypeIcon = (type) => {
    switch (type) {
      case 'technical': return 'code-tags';
      case 'behavioral': return 'account-heart';
      case 'cultural': return 'account-group';
      case 'final': return 'trophy';
      default: return 'help-circle';
    }
  };

  // Render statistics card
  const renderStatCard = (title, value, icon, color, subtitle = null) => (
    <View style={[styles.statCard, { borderLeftColor: color }]}>
      <View style={styles.statCardContent}>
        <View style={[styles.statIconContainer, { backgroundColor: `${color}20` }]}>
          <MaterialCommunityIcons name={icon} size={24} color={color} />
        </View>
        <View style={styles.statTextContainer}>
          <Text style={styles.statValue}>{value}</Text>
          <Text style={styles.statTitle}>{title}</Text>
          {subtitle && <Text style={styles.statSubtitle}>{subtitle}</Text>}
        </View>
      </View>
    </View>
  );

  // Render interview item
  const renderInterviewItem = ({ item }) => {
    const isSelected = selectedInterviews.includes(item.id);
    
    return (
      <TouchableOpacity
        style={[
          styles.interviewCard,
          isSelected && styles.selectedInterviewCard,
        ]}
        onPress={() => {
          if (bulkSelectionMode) {
            if (isSelected) {
              setSelectedInterviews(prev => prev.filter(id => id !== item.id));
            } else {
              setSelectedInterviews(prev => [...prev, item.id]);
            }
          } else {
            setSelectedInterview(item);
            setDetailModalVisible(true);
          }
        }}
        onLongPress={() => {
          if (!bulkSelectionMode) {
            setBulkSelectionMode(true);
            setSelectedInterviews([item.id]);
          }
        }}
      >
        {bulkSelectionMode && (
          <View style={styles.selectionIndicator}>
            <MaterialCommunityIcons
              name={isSelected ? 'checkbox-marked' : 'checkbox-blank-outline'}
              size={24}
              color={isSelected ? '#4A90E2' : '#CBD5E1'}
            />
          </View>
        )}
        
        <View style={styles.interviewHeader}>
          <View style={styles.applicantInfo}>
            <View style={styles.applicantNameRow}>
              <Text style={styles.applicantName}>
                {safeGetString(item.applicantName, 'Unknown Applicant')}
              </Text>
              <View style={[styles.priorityBadge, { backgroundColor: getPriorityColor(item.priority) }]}>
                <Text style={styles.priorityText}>
                  {safeGetString(item.priority, 'medium').toUpperCase()}
                </Text>
              </View>
            </View>
            <Text style={styles.positionText}>
              {safeGetString(item.position, 'Unknown Position')} • {safeGetString(item.department, 'General')}
            </Text>
          </View>
          
          <View style={styles.statusContainer}>
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
              <MaterialCommunityIcons
                name={getStatusIcon(item.status)}
                size={14}
                color="#fff"
              />
              <Text style={styles.statusText}>
                {safeGetString(item.status, 'unknown').charAt(0).toUpperCase() + safeGetString(item.status, 'unknown').slice(1)}
              </Text>
            </View>
            
            <View style={styles.interviewTypeBadge}>
              <MaterialCommunityIcons
                name={getInterviewTypeIcon(item.interviewType)}
                size={12}
                color="#4A90E2"
              />
              <Text style={styles.interviewTypeText}>
                {safeGetString(item.interviewType, 'general').charAt(0).toUpperCase() + safeGetString(item.interviewType, 'general').slice(1)}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.interviewDetails}>
          <View style={styles.detailItem}>
            <MaterialCommunityIcons name="calendar" size={16} color="#64748B" />
            <Text style={styles.detailText}>
              {formatDateTime(item.date, item.time)}
            </Text>
          </View>
          <View style={styles.detailItem}>
            <MaterialCommunityIcons name="clock-outline" size={16} color="#64748B" />
            <Text style={styles.detailText}>
              {safeGetNumber(item.duration, 60)} minutes
            </Text>
          </View>
          <View style={styles.detailItem}>
            <MaterialCommunityIcons name="email-outline" size={16} color="#64748B" />
            <Text style={styles.detailText}>
              {safeGetString(item.applicantEmail, 'No email')}
            </Text>
          </View>
        </View>

        {item.status === 'completed' && safeGetNumber(item.rating, 0) > 0 && (
          <View style={styles.ratingContainer}>
            <Text style={styles.ratingLabel}>Rating:</Text>
            <View style={styles.starsContainer}>
              {[1, 2, 3, 4, 5].map(star => (
                <MaterialCommunityIcons
                  key={star}
                  name={star <= safeGetNumber(item.rating, 0) ? 'star' : 'star-outline'}
                  size={16}
                  color={star <= safeGetNumber(item.rating, 0) ? '#F59E0B' : '#CBD5E1'}
                />
              ))}
            </View>
            <Text style={styles.ratingValue}>
              {safeGetNumber(item.rating, 0).toFixed(1)}
            </Text>
          </View>
        )}

        <View style={styles.interviewActions}>
          {(item.status === 'scheduled' || item.status === 'pending') && (
            <TouchableOpacity
              style={[styles.actionButton, styles.joinButton]}
              onPress={() => handleJoinInterview(item)}
            >
              <MaterialCommunityIcons name="video" size={16} color="#fff" />
              <Text style={styles.joinButtonText}>
                <Text>{item.status === 'scheduled' ? 'Join Interview' : 'Setup Interview'}</Text>
              </Text>
            </TouchableOpacity>
          )}
          
          {item.status === 'completed' && (
            <TouchableOpacity
              style={[styles.actionButton, styles.viewButton]}
              onPress={() => {
                setSelectedInterview(item);
                setDetailModalVisible(true);
              }}
            >
              <MaterialCommunityIcons name="eye" size={16} color="#4A90E2" />
              <Text style={styles.viewButtonText}>View Results</Text>
            </TouchableOpacity>
          )}
          
          <TouchableOpacity
            style={[styles.actionButton, styles.editButton]}
            onPress={() => {
              // Edit functionality would be implemented here
              Alert.alert('Edit Interview', 'Edit functionality would be implemented here');
            }}
          >
            <MaterialCommunityIcons name="pencil" size={16} color="#64748B" />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled': return 'calendar-check';
      case 'completed': return 'check-circle';
      case 'cancelled': return 'close-circle';
      case 'pending': return 'clock-outline';
      default: return 'help-circle';
    }
  };

  // Render filter tab
  const renderFilterTab = (label, value, count = null) => (
    <TouchableOpacity
      style={[
        styles.filterTab,
        filterStatus === value && styles.activeFilterTab,
      ]}
      onPress={() => setFilterStatus(value)}
    >
      <Text
        style={[
          styles.filterTabText,
          filterStatus === value && styles.activeFilterTabText,
        ]}
      >
        {label}
      </Text>
      {count !== null && (
        <View style={[
          styles.filterTabBadge,
          filterStatus === value && styles.activeFilterTabBadge,
        ]}>
          <Text style={[
            styles.filterTabBadgeText,
            filterStatus === value && styles.activeFilterTabBadgeText,
          ]}>
            {count}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );

  // Render new interview form
  const renderNewInterviewForm = () => (
    <ScrollView style={styles.formContainer} contentContainerStyle={styles.formScrollContent}>
      {/* Applicant Information */}
      <View style={styles.formSection}>
        <Text style={styles.formSectionTitle}>Applicant Information</Text>
        
        <Text style={styles.formLabel}>Full Name *</Text>
        <TextInput
          style={styles.textInput}
          value={newInterview.applicantName}
          onChangeText={text => setNewInterview({ ...newInterview, applicantName: text })}
          placeholder="Enter applicant's full name"
          placeholderTextColor="#94A3B8"
        />

        <Text style={styles.formLabel}>Email Address *</Text>
        <TextInput
          style={styles.textInput}
          value={newInterview.applicantEmail}
          onChangeText={text => setNewInterview({ ...newInterview, applicantEmail: text })}
          placeholder="applicant@example.com"
          placeholderTextColor="#94A3B8"
          keyboardType="email-address"
          autoCapitalize="none"
        />

        <Text style={styles.formLabel}>Phone Number</Text>
        <TextInput
          style={styles.textInput}
          value={newInterview.applicantPhone}
          onChangeText={text => setNewInterview({ ...newInterview, applicantPhone: text })}
          placeholder="+44 7700 900000"
          placeholderTextColor="#94A3B8"
          keyboardType="phone-pad"
        />
      </View>

      {/* Position Information */}
      <View style={styles.formSection}>
        <Text style={styles.formSectionTitle}>Position Information</Text>
        
        <Text style={styles.formLabel}>Position *</Text>
        <TextInput
          style={styles.textInput}
          value={newInterview.position}
          onChangeText={text => setNewInterview({ ...newInterview, position: text })}
          placeholder="e.g., Senior Software Engineer"
          placeholderTextColor="#94A3B8"
        />

        <Text style={styles.formLabel}>Department</Text>
        <TextInput
          style={styles.textInput}
          value={newInterview.department}
          onChangeText={text => setNewInterview({ ...newInterview, department: text })}
          placeholder="e.g., Engineering"
          placeholderTextColor="#94A3B8"
        />

        <Text style={styles.formLabel}>Interview Type</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.typeSelector}>
          {['technical', 'behavioral', 'cultural', 'final'].map(type => (
            <TouchableOpacity
              key={type}
              style={[
                styles.typeSelectorButton,
                newInterview.interviewType === type && styles.selectedTypeSelectorButton,
              ]}
              onPress={() => setNewInterview({ ...newInterview, interviewType: type })}
            >
              <MaterialCommunityIcons
                name={getInterviewTypeIcon(type)}
                size={16}
                color={newInterview.interviewType === type ? '#ffffff' : '#4A90E2'}
              />
              <Text style={[
                styles.typeSelectorText,
                newInterview.interviewType === type && styles.selectedTypeSelectorText,
              ]}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        <Text style={styles.formLabel}>Priority</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.prioritySelector}>
          {['high', 'medium', 'low'].map(priority => (
            <TouchableOpacity
              key={priority}
              style={[
                styles.prioritySelectorButton,
                { borderColor: getPriorityColor(priority) },
                newInterview.priority === priority && { backgroundColor: getPriorityColor(priority) },
              ]}
              onPress={() => setNewInterview({ ...newInterview, priority })}
            >
              <Text style={[
                styles.prioritySelectorText,
                { color: newInterview.priority === priority ? '#ffffff' : getPriorityColor(priority) },
              ]}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Schedule Information */}
      <View style={styles.formSection}>
        <Text style={styles.formSectionTitle}>Schedule Information</Text>
        
        <View style={styles.rowContainer}>
          <View style={styles.halfInput}>
            <Text style={styles.formLabel}>Date *</Text>
            <TextInput
              style={styles.textInput}
              value={newInterview.date}
              onChangeText={text => setNewInterview({ ...newInterview, date: text })}
              placeholder="YYYY-MM-DD"
              placeholderTextColor="#94A3B8"
            />
          </View>

          <View style={styles.halfInput}>
            <Text style={styles.formLabel}>Time *</Text>
            <TextInput
              style={styles.textInput}
              value={newInterview.time}
              onChangeText={text => setNewInterview({ ...newInterview, time: text })}
              placeholder="HH:MM"
              placeholderTextColor="#94A3B8"
            />
          </View>
        </View>

        <Text style={styles.formLabel}>Duration (minutes)</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.durationSelector}>
          {['30', '45', '60', '90', '120'].map(duration => (
            <TouchableOpacity
              key={duration}
              style={[
                styles.durationSelectorButton,
                newInterview.duration === duration && styles.selectedDurationSelectorButton,
              ]}
              onPress={() => setNewInterview({ ...newInterview, duration })}
            >
              <Text style={[
                styles.durationSelectorText,
                newInterview.duration === duration && styles.selectedDurationSelectorText,
              ]}>
                {duration} min
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Interview Questions */}
      <View style={styles.formSection}>
        <Text style={styles.formSectionTitle}>Interview Questions</Text>
        
        {newInterview.questions.map((question, index) => (
          <View key={index} style={styles.questionInputContainer}>
            <Text style={styles.formLabel}>Question {index + 1}</Text>
            <TextInput
              style={[styles.textInput, styles.questionInput]}
              value={question}
              onChangeText={text => {
                const updatedQuestions = [...newInterview.questions];
                updatedQuestions[index] = text;
                setNewInterview({ ...newInterview, questions: updatedQuestions });
              }}
              placeholder={`Enter question ${index + 1}...`}
              placeholderTextColor="#94A3B8"
              multiline
            />
          </View>
        ))}
      </View>

      {/* Settings */}
      <View style={styles.formSection}>
        <Text style={styles.formSectionTitle}>Interview Settings</Text>
        
        <View style={styles.settingRow}>
          <View style={styles.settingInfo}>
            <Text style={styles.settingTitle}>Enable Recording</Text>
            <Text style={styles.settingDescription}>Record the interview for review and training</Text>
          </View>
          <TouchableOpacity
            style={[styles.toggle, newInterview.recordingEnabled && styles.toggleActive]}
            onPress={() => setNewInterview({ ...newInterview, recordingEnabled: !newInterview.recordingEnabled })}
          >
            <View style={[styles.toggleThumb, newInterview.recordingEnabled && styles.toggleThumbActive]} />
          </TouchableOpacity>
        </View>

        <View style={styles.settingRow}>
          <View style={styles.settingInfo}>
            <Text style={styles.settingTitle}>AI Analysis</Text>
            <Text style={styles.settingDescription}>Analyze applicant responses with AI</Text>
          </View>
          <TouchableOpacity
            style={[styles.toggle, newInterview.aiAnalysisEnabled && styles.toggleActive]}
            onPress={() => setNewInterview({ ...newInterview, aiAnalysisEnabled: !newInterview.aiAnalysisEnabled })}
          >
            <View style={[styles.toggleThumb, newInterview.aiAnalysisEnabled && styles.toggleThumbActive]} />
          </TouchableOpacity>
        </View>

        <View style={styles.settingRow}>
          <View style={styles.settingInfo}>
            <Text style={styles.settingTitle}>Transcription</Text>
            <Text style={styles.settingDescription}>Generate automatic transcripts</Text>
          </View>
          <TouchableOpacity
            style={[styles.toggle, newInterview.transcriptionEnabled && styles.toggleActive]}
            onPress={() => setNewInterview({ ...newInterview, transcriptionEnabled: !newInterview.transcriptionEnabled })}
          >
            <View style={[styles.toggleThumb, newInterview.transcriptionEnabled && styles.toggleThumbActive]} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Notes */}
      <View style={styles.formSection}>
        <Text style={styles.formSectionTitle}>Additional Notes</Text>
        
        <TextInput
          style={[styles.textInput, styles.notesInput]}
          value={newInterview.notes}
          onChangeText={text => setNewInterview({ ...newInterview, notes: text })}
          placeholder="Add any additional notes or special instructions..."
          placeholderTextColor="#94A3B8"
          multiline
          numberOfLines={4}
        />
      </View>

      {/* Form Buttons */}
      <View style={styles.formButtons}>
        <TouchableOpacity
          style={[styles.formButton, styles.cancelButton]}
          onPress={() => setModalVisible(false)}
        >
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.formButton, styles.saveButton]}
          onPress={handleAddInterview}
        >
          <Text style={styles.saveButtonText}>Schedule Interview</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#4A90E2" />
        <Text style={styles.loadingText}>Loading Interviews...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      {/* Header */}
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="white" />
          </TouchableOpacity>
          
          <View style={styles.headerTitleContainer}>
            <Text style={styles.headerTitle}>Video Interview Management</Text>
            <Text style={styles.headerSubtitle}>
              {safeGetString(companyData?.name, 'Your Company')} • {statistics.total} Total Interviews
            </Text>
          </View>
          
          <TouchableOpacity
            style={styles.headerAction}
            onPress={() => setAnalyticsModalVisible(true)}
          >
            <MaterialCommunityIcons name="chart-line" size={24} color="white" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Content */}
      <Animated.View style={[styles.content, { opacity: fadeAnimation }]}>
        {/* Statistics Overview */}
        <View style={styles.statsSection}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.statsScroll}
            contentContainerStyle={styles.statsScrollContent}
          >
            {renderStatCard('Total', statistics.total, 'calendar-multiple', '#4A90E2')}
            {renderStatCard('Scheduled', statistics.scheduled, 'calendar-check', '#10B981')}
            {renderStatCard('Completed', statistics.completed, 'check-circle', '#059669')}
            {renderStatCard('Pending', statistics.pending, 'clock-outline', '#F59E0B')}
            {renderStatCard('Avg Rating', statistics.averageRating.toFixed(1), 'star', '#F59E0B', '/ 5.0')}
            {renderStatCard('Success Rate', `${statistics.successRate}%`, 'trending-up', '#10B981')}
          </ScrollView>
        </View>

        {/* Controls */}
        <View style={styles.controlsSection}>
          {/* Search and Add Button */}
          <View style={styles.searchContainer}>
            <View style={styles.searchInputContainer}>
              <MaterialCommunityIcons name="magnify" size={20} color="#64748B" />
              <TextInput
                style={styles.searchInput}
                placeholder="Search interviews..."
                placeholderTextColor="#94A3B8"
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery('')}>
                  <MaterialCommunityIcons name="close" size={20} color="#64748B" />
                </TouchableOpacity>
              )}
            </View>
            
            <TouchableOpacity
              style={styles.addButton}
              onPress={() => setModalVisible(true)}
            >
              <MaterialCommunityIcons name="plus" size={20} color="#fff" />
            </TouchableOpacity>
          </View>

          {/* Filter Tabs */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.filterTabs}
            contentContainerStyle={styles.filterTabsContent}
          >
            {renderFilterTab('All', 'all', statistics.total)}
            {renderFilterTab('Scheduled', 'scheduled', statistics.scheduled)}
            {renderFilterTab('Completed', 'completed', statistics.completed)}
            {renderFilterTab('Pending', 'pending', statistics.pending)}
            {renderFilterTab('Cancelled', 'cancelled', statistics.cancelled)}
          </ScrollView>

          {/* Sort and Bulk Actions */}
          <View style={styles.actionsRow}>
            <TouchableOpacity
              style={styles.sortButton}
              onPress={() => {
                // Cycle through sort options
                const sortOptions = ['date', 'name', 'position', 'rating', 'priority'];
                const currentIndex = sortOptions.indexOf(sortBy);
                const nextIndex = (currentIndex + 1) % sortOptions.length;
                setSortBy(sortOptions[nextIndex]);
              }}
            >
              <MaterialCommunityIcons name="sort" size={16} color="#4A90E2" />
              <Text style={styles.sortButtonText}>
                Sort by {sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}
              </Text>
              <MaterialCommunityIcons
                name={sortOrder === 'asc' ? 'arrow-up' : 'arrow-down'}
                size={16}
                color="#4A90E2"
              />
            </TouchableOpacity>

            {bulkSelectionMode && (
              <TouchableOpacity
                style={styles.bulkActionButton}
                onPress={() => setBulkActionModalVisible(true)}
              >
                <MaterialCommunityIcons name="checkbox-multiple-marked" size={16} color="#EF4444" />
                <Text style={styles.bulkActionButtonText}>
                  {selectedInterviews.length} Selected
                </Text>
              </TouchableOpacity>
            )}

            {bulkSelectionMode && (
              <TouchableOpacity
                style={styles.cancelBulkButton}
                onPress={() => {
                  setBulkSelectionMode(false);
                  setSelectedInterviews([]);
                }}
              >
                <Text style={styles.cancelBulkButtonText}>Cancel</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Interview List */}
        <FlatList
          data={filteredInterviews}
          renderItem={renderInterviewItem}
          keyExtractor={(item) => item.id}
          style={styles.interviewList}
          contentContainerStyle={styles.interviewListContent}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#4A90E2']} />
          }
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <MaterialCommunityIcons name="calendar-blank" size={64} color="#CBD5E1" />
              <Text style={styles.emptyTitle}>No Interviews Found</Text>
              <Text style={styles.emptySubtitle}>
                {searchQuery || filterStatus !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Schedule your first video interview to get started'}
              </Text>
            </View>
          }
        />
      </Animated.View>

      {/* New Interview Modal */}
      <Modal
        visible={modalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Schedule New Interview</Text>
            <TouchableOpacity onPress={() => setModalVisible(false)}>
              <AntDesign name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          {renderNewInterviewForm()}
        </View>
      </Modal>

      {/* Interview Detail Modal */}
      <Modal
        visible={detailModalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setDetailModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Interview Details</Text>
            <TouchableOpacity onPress={() => setDetailModalVisible(false)}>
              <AntDesign name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          {selectedInterview && (
            <ScrollView
              style={styles.modalContent}
              contentContainerStyle={styles.modalScrollContent}
            >
              {/* Applicant Information */}
              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>Applicant Information</Text>
                <View style={styles.detailCard}>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Name:</Text>
                    <Text style={styles.detailValue}>
                      {safeGetString(selectedInterview.applicantName, 'Unknown')}
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Email:</Text>
                    <Text style={styles.detailValue}>
                      {safeGetString(selectedInterview.applicantEmail, 'Not provided')}
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Phone:</Text>
                    <Text style={styles.detailValue}>
                      {safeGetString(selectedInterview.applicantPhone, 'Not provided')}
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Position:</Text>
                    <Text style={styles.detailValue}>
                      {safeGetString(selectedInterview.position, 'Unknown')}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Interview Details */}
              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>Interview Details</Text>
                <View style={styles.detailCard}>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Date & Time:</Text>
                    <Text style={styles.detailValue}>
                      {formatDateTime(selectedInterview.date, selectedInterview.time)}
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Duration:</Text>
                    <Text style={styles.detailValue}>
                      {safeGetNumber(selectedInterview.duration, 60)} minutes
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Type:</Text>
                    <Text style={styles.detailValue}>
                      {safeGetString(selectedInterview.interviewType, 'general').charAt(0).toUpperCase() + safeGetString(selectedInterview.interviewType, 'general').slice(1)}
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Priority:</Text>
                    <Text style={[styles.detailValue, { color: getPriorityColor(selectedInterview.priority) }]}>
                      {safeGetString(selectedInterview.priority, 'medium').charAt(0).toUpperCase() + safeGetString(selectedInterview.priority, 'medium').slice(1)}
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Status:</Text>
                    <View style={[styles.statusBadge, { backgroundColor: getStatusColor(selectedInterview.status) }]}>
                      <Text style={styles.statusText}>
                        {safeGetString(selectedInterview.status, 'unknown').charAt(0).toUpperCase() + safeGetString(selectedInterview.status, 'unknown').slice(1)}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>

              {/* Questions */}
              {safeGetArray(selectedInterview.questions, []).length > 0 && (
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>Interview Questions</Text>
                  <View style={styles.detailCard}>
                    {safeGetArray(selectedInterview.questions, []).map((question, index) => (
                      <View key={index} style={styles.questionItem}>
                        <Text style={styles.questionNumber}>Q{index + 1}:</Text>
                        <Text style={styles.questionText}>{question}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Results (for completed interviews) */}
              {selectedInterview.status === 'completed' && (
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>Interview Results</Text>
                  <View style={styles.detailCard}>
                    {safeGetNumber(selectedInterview.rating, 0) > 0 && (
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Rating:</Text>
                        <View style={styles.ratingContainer}>
                          <View style={styles.starsContainer}>
                            {[1, 2, 3, 4, 5].map(star => (
                              <MaterialCommunityIcons
                                key={star}
                                name={star <= safeGetNumber(selectedInterview.rating, 0) ? 'star' : 'star-outline'}
                                size={16}
                                color={star <= safeGetNumber(selectedInterview.rating, 0) ? '#F59E0B' : '#CBD5E1'}
                              />
                            ))}
                          </View>
                          <Text style={styles.ratingValue}>
                            {safeGetNumber(selectedInterview.rating, 0).toFixed(1)}/5.0
                          </Text>
                        </View>
                      </View>
                    )}
                    
                    {safeGetString(selectedInterview.feedback, '').length > 0 && (
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Feedback:</Text>
                        <Text style={styles.detailValue}>
                          {safeGetString(selectedInterview.feedback, 'No feedback provided')}
                        </Text>
                      </View>
                    )}
                    
                    {selectedInterview.recordingUrl && (
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Recording:</Text>
                        <TouchableOpacity style={styles.linkButton}>
                          <MaterialCommunityIcons name="play-circle" size={16} color="#4A90E2" />
                          <Text style={styles.linkButtonText}>View Recording</Text>
                        </TouchableOpacity>
                      </View>
                    )}
                    
                    {selectedInterview.transcriptUrl && (
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Transcript:</Text>
                        <TouchableOpacity style={styles.linkButton}>
                          <MaterialCommunityIcons name="file-document" size={16} color="#4A90E2" />
                          <Text style={styles.linkButtonText}>View Transcript</Text>
                        </TouchableOpacity>
                      </View>
                    )}
                    
                    {selectedInterview.aiAnalysisUrl && (
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>AI Analysis:</Text>
                        <TouchableOpacity style={styles.linkButton}>
                          <MaterialCommunityIcons name="brain" size={16} color="#4A90E2" />
                          <Text style={styles.linkButtonText}>View Analysis</Text>
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                </View>
              )}

              {/* Action Buttons */}
              <View style={styles.detailActions}>
                {(selectedInterview.status === 'scheduled' || selectedInterview.status === 'pending') && (
                  <TouchableOpacity
                    style={[styles.detailActionButton, styles.joinDetailButton]}
                    onPress={() => {
                      setDetailModalVisible(false);
                      handleJoinInterview(selectedInterview);
                    }}
                  >
                    <MaterialCommunityIcons name="video" size={20} color="#fff" />
                    <Text style={styles.joinDetailButtonText}>Join Interview</Text>
                  </TouchableOpacity>
                )}
                
                <TouchableOpacity
                  style={[styles.detailActionButton, styles.editDetailButton]}
                  onPress={() => {
                    Alert.alert('Edit Interview', 'Edit functionality would be implemented here');
                  }}
                >
                  <MaterialCommunityIcons name="pencil" size={20} color="#4A90E2" />
                  <Text style={styles.editDetailButtonText}>Edit Interview</Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[styles.detailActionButton, styles.cancelDetailButton]}
                  onPress={() => {
                    Alert.alert(
                      'Cancel Interview',
                      'Are you sure you want to cancel this interview?',
                      [
                        { text: 'No', style: 'cancel' },
                        {
                          text: 'Yes',
                          style: 'destructive',
                          onPress: () => {
                            handleStatusChange(selectedInterview.id, 'cancelled');
                            setDetailModalVisible(false);
                          },
                        },
                      ]
                    );
                  }}
                >
                  <MaterialCommunityIcons name="cancel" size={20} color="#EF4444" />
                  <Text style={styles.cancelDetailButtonText}>Cancel Interview</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          )}
        </View>
      </Modal>

      {/* Bulk Action Modal */}
      <Modal
        visible={bulkActionModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setBulkActionModalVisible(false)}
      >
        <View style={styles.bulkActionOverlay}>
          <View style={styles.bulkActionModal}>
            <Text style={styles.bulkActionTitle}>
              Bulk Actions ({selectedInterviews.length} selected)
            </Text>
            
            <TouchableOpacity
              style={styles.bulkActionOption}
              onPress={() => handleBulkAction('cancel')}
            >
              <MaterialCommunityIcons name="cancel" size={24} color="#F59E0B" />
              <Text style={styles.bulkActionOptionText}>Cancel Interviews</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.bulkActionOption}
              onPress={() => handleBulkAction('reschedule')}
            >
              <MaterialCommunityIcons name="calendar-edit" size={24} color="#4A90E2" />
              <Text style={styles.bulkActionOptionText}>Reschedule Interviews</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.bulkActionOption}
              onPress={() => handleBulkAction('delete')}
            >
              <MaterialCommunityIcons name="delete" size={24} color="#EF4444" />
              <Text style={styles.bulkActionOptionText}>Delete Interviews</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.bulkActionCancel}
              onPress={() => setBulkActionModalVisible(false)}
            >
              <Text style={styles.bulkActionCancelText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
  },
  header: {
    paddingTop: 10,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  headerAction: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  content: {
    flex: 1,
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    marginTop: -10,
  },
  statsSection: {
    paddingVertical: 20,
  },
  statsScroll: {
    paddingLeft: 20,
  },
  statsScrollContent: {
    paddingRight: 20,
  },
  statCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginRight: 12,
    minWidth: 140,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  statCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  statTextContainer: {
    flex: 1,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  statTitle: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 2,
  },
  statSubtitle: {
    fontSize: 10,
    color: '#94a3b8',
    marginTop: 1,
  },
  controlsSection: {
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#0f172a',
    marginLeft: 8,
  },
  addButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#4A90E2',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  filterTabs: {
    marginBottom: 16,
  },
  filterTabsContent: {
    paddingHorizontal: 4,
  },
  filterTab: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  activeFilterTab: {
    backgroundColor: '#4A90E2',
    borderColor: '#4A90E2',
  },
  filterTabText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '600',
  },
  activeFilterTabText: {
    color: '#ffffff',
  },
  filterTabBadge: {
    backgroundColor: '#e2e8f0',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 6,
  },
  activeFilterTabBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
  filterTabBadgeText: {
    fontSize: 10,
    color: '#64748B',
    fontWeight: 'bold',
  },
  activeFilterTabBadgeText: {
    color: '#ffffff',
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sortButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f8ff',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#4A90E2',
  },
  sortButtonText: {
    fontSize: 14,
    color: '#4A90E2',
    fontWeight: '600',
    marginHorizontal: 4,
  },
  bulkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fef2f2',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#EF4444',
  },
  bulkActionButtonText: {
    fontSize: 14,
    color: '#EF4444',
    fontWeight: '600',
    marginLeft: 4,
  },
  cancelBulkButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  cancelBulkButtonText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '600',
  },
  interviewList: {
    flex: 1,
  },
  interviewListContent: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  interviewCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  selectedInterviewCard: {
    borderColor: '#4A90E2',
    backgroundColor: '#f0f8ff',
  },
  selectionIndicator: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1,
  },
  interviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  applicantInfo: {
    flex: 1,
    marginRight: 12,
  },
  applicantNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  applicantName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    flex: 1,
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
    marginLeft: 8,
  },
  priorityText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  positionText: {
    fontSize: 14,
    color: '#4A90E2',
    fontWeight: '600',
  },
  statusContainer: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 4,
  },
  statusText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: 4,
  },
  interviewTypeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f8ff',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#4A90E2',
  },
  interviewTypeText: {
    fontSize: 10,
    color: '#4A90E2',
    fontWeight: '600',
    marginLeft: 2,
  },
  interviewDetails: {
    marginBottom: 12,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  detailText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 8,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  ratingLabel: {
    fontSize: 14,
    color: '#64748B',
    marginRight: 8,
  },
  starsContainer: {
    flexDirection: 'row',
    marginRight: 8,
  },
  ratingValue: {
    fontSize: 14,
    color: '#0f172a',
    fontWeight: '600',
  },
  interviewActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginLeft: 8,
  },
  joinButton: {
    backgroundColor: '#4A90E2',
  },
  joinButtonText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 4,
  },
  viewButton: {
    backgroundColor: '#f0f8ff',
    borderWidth: 1,
    borderColor: '#4A90E2',
  },
  viewButtonText: {
    fontSize: 14,
    color: '#4A90E2',
    fontWeight: '600',
    marginLeft: 4,
  },
  editButton: {
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#64748B',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#94a3b8',
    textAlign: 'center',
    lineHeight: 20,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  modalContent: {
    flex: 1,
  },
  modalScrollContent: {
    padding: 20,
  },
  formContainer: {
    flex: 1,
  },
  formScrollContent: {
    paddingBottom: 40,
  },
  formSection: {
    marginBottom: 24,
  },
  formSectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  textInput: {
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0f172a',
    marginBottom: 16,
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  halfInput: {
    width: '48%',
  },
  typeSelector: {
    marginBottom: 16,
  },
  typeSelectorButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f8ff',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#4A90E2',
  },
  selectedTypeSelectorButton: {
    backgroundColor: '#4A90E2',
  },
  typeSelectorText: {
    fontSize: 14,
    color: '#4A90E2',
    fontWeight: '600',
    marginLeft: 4,
  },
  selectedTypeSelectorText: {
    color: '#ffffff',
  },
  prioritySelector: {
    marginBottom: 16,
  },
  prioritySelectorButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 8,
    borderWidth: 1,
  },
  prioritySelectorText: {
    fontSize: 14,
    fontWeight: '600',
  },
  durationSelector: {
    marginBottom: 16,
  },
  durationSelectorButton: {
    backgroundColor: '#f8fafc',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  selectedDurationSelectorButton: {
    backgroundColor: '#4A90E2',
    borderColor: '#4A90E2',
  },
  durationSelectorText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '600',
  },
  selectedDurationSelectorText: {
    color: '#ffffff',
  },
  questionInputContainer: {
    marginBottom: 16,
  },
  questionInput: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  settingInfo: {
    flex: 1,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 2,
  },
  settingDescription: {
    fontSize: 14,
    color: '#64748B',
  },
  toggle: {
    width: 48,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#e2e8f0',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  toggleActive: {
    backgroundColor: '#4A90E2',
  },
  toggleThumb: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    transform: [{ translateX: 20 }],
  },
  notesInput: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  formButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  formButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    marginRight: 8,
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#64748B',
    fontWeight: '600',
  },
  saveButton: {
    backgroundColor: '#4A90E2',
    marginLeft: 8,
  },
  saveButtonText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
  },
  detailSection: {
    marginBottom: 24,
  },
  detailSectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 12,
  },
  detailCard: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  detailLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
    minWidth: 100,
  },
  detailValue: {
    fontSize: 14,
    color: '#0f172a',
    flex: 1,
  },
  questionItem: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  questionNumber: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#4A90E2',
    marginRight: 8,
    minWidth: 24,
  },
  questionText: {
    fontSize: 14,
    color: '#0f172a',
    flex: 1,
    lineHeight: 20,
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f8ff',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#4A90E2',
  },
  linkButtonText: {
    fontSize: 12,
    color: '#4A90E2',
    fontWeight: '600',
    marginLeft: 4,
  },
  detailActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  detailActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    marginBottom: 8,
    minWidth: '48%',
    justifyContent: 'center',
  },
  joinDetailButton: {
    backgroundColor: '#4A90E2',
  },
  joinDetailButtonText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 8,
  },
  editDetailButton: {
    backgroundColor: '#f0f8ff',
    borderWidth: 1,
    borderColor: '#4A90E2',
  },
  editDetailButtonText: {
    fontSize: 16,
    color: '#4A90E2',
    fontWeight: '600',
    marginLeft: 8,
  },
  cancelDetailButton: {
    backgroundColor: '#fef2f2',
    borderWidth: 1,
    borderColor: '#EF4444',
    width: '100%',
  },
  cancelDetailButtonText: {
    fontSize: 16,
    color: '#EF4444',
    fontWeight: '600',
    marginLeft: 8,
  },
  bulkActionOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  bulkActionModal: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 24,
    margin: 20,
    minWidth: 280,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  bulkActionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 20,
    textAlign: 'center',
  },
  bulkActionOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: '#f8fafc',
  },
  bulkActionOptionText: {
    fontSize: 16,
    color: '#0f172a',
    marginLeft: 12,
  },
  bulkActionCancel: {
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  bulkActionCancelText: {
    fontSize: 16,
    color: '#64748B',
    fontWeight: '600',
  },
});

export default VideoInterviewManagementScreen;